import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useApp } from "@/context/AppContext";
import { useLiveResource } from "@/hooks/useLiveResource";
import { GROWTH_TEXT_LIMITS } from "@/growth/growthLimits";
import { monthWindow, todayKey } from "@/growth/growthMath";

const NO_ARGS = [];

const count = (n) => n.toLocaleString("en-US");
// What to check, by the field the server named in a 400. Each says the limit, so the person knows what to change.
const FIELD_HINTS = {
  title: `the title (${count(GROWTH_TEXT_LIMITS.title)} characters at most)`,
  hypothesis: `“What we would learn” (${count(GROWTH_TEXT_LIMITS.prose)} characters at most)`,
  channel: `the channel (${count(GROWTH_TEXT_LIMITS.short)} characters at most)`,
  answer: `your answer (${count(GROWTH_TEXT_LIMITS.prose)} characters at most)`,
};
function fieldHint(message) {
  const field = /Check the (.+?) field/.exec(String(message || ""))?.[1] || "";
  if (/^(goals|remove)\[/.test(field)) return "the targets (numbers above zero)";
  return FIELD_HINTS[field] || "what you typed";
}

/** A short sentence for a failed action, in the words the API gave us. Only a failure that may pass later says to retry. */
export function actionSentence(error) {
  if (error?.code === "demo_view_read_only") return error.message;
  if (error?.code === "growth_invalid") return `That did not save. Check ${fieldHint(error.message)}.`;
  if ((error?.code === "growth_initiative_not_found" || error?.code === "growth_question_not_found") && error.message) return error.message;
  if (error?.code === "growth_manage_forbidden") return "Only an owner or admin can change targets.";
  if (error?.code === "growth_act_forbidden") return "Your role can read the plan but not act on it.";
  if (error?.code === "growth_transition_invalid") return "That step is no longer possible; the item has moved on.";
  if (error?.status === 413) return "That is too long to save.";
  return "That did not save. Try again in a moment.";
}

/**
 * The Test section's one resource plus the client's actions on it. Every page mounts this once; the payload is small,
 * so a single read serves the brief, goals, plan, score and experiments. Actions reload quietly afterwards.
 */
export function useGrowth({ refreshMs = 60_000, reportingWindow = null } = {}) {
  const { api, activeClient } = useApp();
  const resource = useLiveResource("getGrowth", reportingWindow ? [reportingWindow] : NO_ARGS, { refreshMs });
  const [busyKey, setBusyKey] = useState(null);
  const data = resource.data || null;
  const timezone = data?.settings?.timezone || null;
  const [, setCalendarDay] = useState(() => todayKey(timezone));
  const today = todayKey(timezone);
  useEffect(() => {
    // The calendar must advance even if polling is paused or every request fails.
    const updateDay = () => setCalendarDay(todayKey(timezone));
    updateDay();
    const timer = window.setInterval(updateDay, 60_000);
    window.addEventListener("focus", updateDay);
    document.addEventListener("visibilitychange", updateDay);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", updateDay);
      document.removeEventListener("visibilitychange", updateDay);
    };
  }, [timezone]);
  const month = useMemo(() => monthWindow(today), [today]);
  const reload = resource.reload;

  // Keys of actions on their way to the server. A ref, not state, so a second click in the same frame (before the
  // button re-renders as disabled) is caught too and never sends a second request.
  const inFlight = useRef(new Set());
  const run = useCallback(async (key, action, success) => {
    if (inFlight.current.has(key)) return null;
    inFlight.current.add(key);
    setBusyKey(key);
    try {
      const result = await action();
      if (success) toast.success(success);
      try { await reload(); } catch { /* keep the last good data */ }
      return result;
    } catch (error) {
      toast.error(actionSentence(error));
      // The item moved on or went away: re-read it so the open view shows where it is now.
      if (error?.status === 404 || error?.status === 409) { try { await reload(); } catch { /* keep the last good data */ } }
      return null;
    } finally {
      inFlight.current.delete(key);
      setBusyKey(null);
    }
  }, [reload]);

  const actions = useMemo(() => ({
    setGoals: (period, goals, remove = []) => run("goals", () => api.setGrowthGoals({ period, goals, remove }), "Targets saved"),
    approveInitiative: (id) => run(`initiative:${id}`, () => api.updateGrowthInitiative(id, "approve"), "Approved"),
    completeInitiative: (id) => run(`initiative:${id}`, () => api.updateGrowthInitiative(id, "done"), "Marked done"),
    parkInitiative: (id) => run(`initiative:${id}`, () => api.updateGrowthInitiative(id, "park"), "Parked"),
    reopenInitiative: (id) => run(`initiative:${id}`, () => api.updateGrowthInitiative(id, "reopen"), "Back in the plan"),
    addExperiment: (idea) => run("experiment", () => api.addGrowthExperiment(idea), "Idea added"),
    answerQuestion: (id, answer) => run(`question:${id}`, () => api.answerGrowthQuestion(id, answer), "Answer saved"),
    skipQuestion: (id) => run(`question:${id}`, () => api.skipGrowthQuestion(id), "Skipped"),
  }), [api, run]);

  return { resource, data, today, month, timezone, clientSlug: activeClient?.clientSlug ?? null, busyKey, actions, reload };
}

/** The workspace's configured agent name, read once per visit (the overlay's name, never a constant). */
export function useAgentName() {
  const profile = useLiveResource("getAgentProfile", NO_ARGS, { refreshMs: 0 });
  return profile.data?.agent?.name || "Your agent";
}
